import { hoursToSeconds } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import uuid from 'react-uuid';
import { useDebouncedCallback } from 'use-debounce';
import { ItemTypes } from '../../components/gantt/dragDrop/types';
import { GanttTimePicker } from '../../components/gantt/gantt/GanttTimePicker';
import { itemTypeToColor } from '../../helpers/dailyHelper';
import { Contract } from '../../services/contract/contracts.types';
import { DailyEditingType, WorkingShift, WorkingShiftSubsection } from '../../types/daily.types';
import { useBuildGanttSubSections } from './hooks/useBuildGanttSubSections';

export interface GhostSubShift {
  startTime: number;
  endTime: number;
  typeContext?: string;
}

export interface GhostShift {
  startTime: number;
  endTime: number;
  subs?: GhostSubShift[];
}

interface Props {
  shift: WorkingShift;
  ghostShifts?: GhostShift[];
  updateShift: (newShift: WorkingShift) => void;
  type: DailyEditingType;
  readOnly?: boolean;
  useColorizedBar?: boolean;
  hasSetEmployee?: boolean;
  contract?: Contract;
  otherShiftsDuration?: number;
}

const mapToItemType = (editType: DailyEditingType) => {
  if (editType === DailyEditingType.REASSIGNED) return ItemTypes.REASSIGNED;
  return editType === DailyEditingType.ROSTER ? ItemTypes.ROSTER_SHIFT : ItemTypes.TEMPLATE_SHIFT;
};

export const ShiftTimePicker: FC<Props> = ({
  shift: extShift,
  updateShift,
  type,
  readOnly,
  useColorizedBar,
  hasSetEmployee,
  contract,
  otherShiftsDuration
}) => {
  const [currentExtShift, setCurrentExtShift] = useState<WorkingShift>(extShift);
  const [shift, setShift] = useState<WorkingShift>(currentExtShift);

  const debouncedUpdate = useDebouncedCallback(updateShift, 100);

  useEffect(() => {
    if (extShift !== currentExtShift) {
      setCurrentExtShift(extShift);
      setShift(extShift);
    } else if (JSON.stringify(shift) !== JSON.stringify(currentExtShift)) {
      debouncedUpdate(shift);
    }
  }, [shift, extShift, currentExtShift, debouncedUpdate]);

  const mainSection =
    shift.start !== undefined && shift.end !== undefined
      ? {
          id: shift.id,
          startTime: shift.start,
          endTime: shift.end,
          color: itemTypeToColor(mapToItemType(type), useColorizedBar, hasSetEmployee) || '#000000',
          onChangeTimes: (newStart: number, newEnd: number) => {
            const newShift = { ...shift };
            newShift.start = newStart;
            newShift.end = newEnd;
            setShift(newShift);
          },
          clear: () => {
            const newShift = { ...shift };
            newShift.start = undefined;
            newShift.end = undefined;
            newShift.breaks = [];
            newShift.tasks = [];
            setShift(newShift);
          }
        }
      : undefined;

  const subsections = useBuildGanttSubSections(shift, setShift, type);

  const onShiftAll = (change: number) => {
    const newShift = { ...shift };

    if (typeof newShift.start === 'number' && typeof newShift.end === 'number') {
      newShift.start = newShift.start + change;
      newShift.end = newShift.end + change;

      newShift.tasks = newShift.tasks.map(task => {
        const newTask = { ...task };
        newTask.start = newTask.start + change;
        newTask.end = newTask.end + change;

        return newTask;
      });

      newShift.breaks = newShift.breaks.map(brek => {
        const newBrek = { ...brek };
        newBrek.start = newBrek.start + change;
        newBrek.end = newBrek.end + change;

        return newBrek;
      });
    }

    setShift(newShift);
  };

  const remainingAssignableTime = contract?.maxHoursPerDay
    ? Math.round(hoursToSeconds(contract.maxHoursPerDay)) - (otherShiftsDuration || 0)
    : undefined;
  const maxTimeFromContract = contract ? Math.round(hoursToSeconds(contract.maxHoursPerShift)) : undefined;
  let maxTime = remainingAssignableTime || maxTimeFromContract;

  if (remainingAssignableTime !== undefined && maxTimeFromContract !== undefined) {
    maxTime = Math.min(remainingAssignableTime, maxTimeFromContract);
  }

  return (
    <GanttTimePicker
      customMaxTime={maxTime}
      mainSection={mainSection}
      readOnly={readOnly}
      subSections={subsections}
      onShiftAll={onShiftAll}
      addSection={(start: number, end: number, context: string, main?: boolean) => {
        if (readOnly) {
          return;
        }

        if (main) {
          const newShift = { ...shift };
          newShift.start = start;
          newShift.end = end;
          setShift(newShift);
        } else {
          const newShift = { ...shift };

          const newSubsection: WorkingShiftSubsection = {
            id: `CREATE-${uuid()}`,
            type: '',
            start,
            end
          };

          if (context === ItemTypes.BREAK) {
            newSubsection.type = ItemTypes.BREAK;
            const newBreaks = [...newShift.breaks];
            newBreaks.push(newSubsection);
            newShift.breaks = newBreaks;
          } else {
            newSubsection.type = ItemTypes.TASK;
            newSubsection.typeContext = context;
            const newTasks = [...newShift.tasks];
            newTasks.push(newSubsection);
            newShift.tasks = newTasks;
          }

          setShift(newShift);
        }
      }}
    />
  );
};
